import React, { useEffect, useRef, useState } from 'react';
import { User } from '@supabase/supabase-js';
import { supabase, logout } from '../lib/supabase';
import { getQuizHistory, deleteQuizSession, QuizSession } from '../lib/quizSupabase';
import { useTheme } from '../lib/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import {
  LogOut, Sun, Moon, LayoutDashboard, Mail,
  ShieldCheck, FileText, Info, ChevronRight, Crown, Sparkles,
  Camera, Loader2, Trash2,
} from 'lucide-react';

interface ProfileProps { user: User; }

export default function Profile({ user }: ProfileProps) {
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [isPremium, setIsPremium] = useState(false);
  const [quizHistory, setQuizHistory] = useState<QuizSession[]>([]);
  const [quizHistoryLoading, setQuizHistoryLoading] = useState(true);
  const [deletingQuizId, setDeletingQuizId] = useState<string | null>(null);

  const handleDeleteQuiz = async (id: string) => {
    if (!confirm('Delete this Yhuu Test from your history? This cannot be undone.')) return;
    setDeletingQuizId(id);
    const ok = await deleteQuizSession(id);
    setDeletingQuizId(null);
    if (ok) setQuizHistory((prev) => prev.filter((s) => s.id !== id));
    else alert('Failed to delete — please try again.');
  };

  // Seed with the OAuth provider's photo (e.g. Google) if there is one, so
  // there's something better than initials before the DB fetch resolves.
  // A custom upload (saved in profiles.avatar_url) takes priority once loaded.
  const [avatarUrl, setAvatarUrl] = useState<string | null>(
    user.user_metadata?.avatar_url || user.user_metadata?.picture || null
  );
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const [avatarError, setAvatarError] = useState('');
  const avatarInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    supabase
      .from('profiles')
      .select('premium_until, avatar_url')
      .eq('id', user.id)
      .maybeSingle()
      .then(({ data }) => {
        if (data?.premium_until && new Date(data.premium_until) > new Date()) {
          setIsPremium(true);
        }
        if (data?.avatar_url) {
          setAvatarUrl(data.avatar_url);
        }
      });
  }, [user.id]);

  const handleAvatarSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      setAvatarError('Please choose an image file.');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setAvatarError('Image must be under 5MB.');
      return;
    }

    setAvatarError('');
    const previousUrl = avatarUrl;
    const localPreview = URL.createObjectURL(file);
    setAvatarUrl(localPreview);
    setUploadingAvatar(true);

    try {
      const ext = file.name.split('.').pop()?.toLowerCase() || 'jpg';
      const path = `${user.id}/avatar-${Date.now()}.${ext}`;

      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(path, file, { contentType: file.type, upsert: true });
      if (uploadError) throw uploadError;

      const { data } = supabase.storage.from('avatars').getPublicUrl(path);

      const { error: dbError } = await supabase
        .from('profiles')
        .upsert({ id: user.id, avatar_url: data.publicUrl }, { onConflict: 'id' });
      if (dbError) throw dbError;

      setAvatarUrl(data.publicUrl);
    } catch (err) {
      console.error('Avatar upload failed:', err);
      setAvatarError('Could not upload photo. Try again.');
      setAvatarUrl(previousUrl);
    } finally {
      setUploadingAvatar(false);
      URL.revokeObjectURL(localPreview);
    }
  };

  useEffect(() => {
    getQuizHistory(user.id)
      .then(setQuizHistory)
      .finally(() => setQuizHistoryLoading(false));
  }, [user.id]);

  const initial = (user.user_metadata?.full_name || user.email || 'U')[0].toUpperCase();
  const displayName = user.user_metadata?.full_name || user.email?.split('@')[0] || 'User';
  const email = user.email || '';
  const joinDate = new Date(user.created_at).toLocaleDateString(undefined, { month: 'long', year: 'numeric' });

  const testLink = `/test?yhuu_uid=${encodeURIComponent(user.id)}&yhuu_name=${encodeURIComponent(displayName)}`;

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <div className="max-w-lg mx-auto py-10 px-4 space-y-6">

      {/* Avatar card */}
      <div
        className="rounded-2xl border border-brand-border p-6 flex items-center gap-5"
        style={{ background: 'var(--color-brand-card)' }}
      >
        <div className="relative flex-shrink-0">
          <input
            ref={avatarInputRef}
            type="file"
            accept="image/*"
            onChange={handleAvatarSelect}
            className="hidden"
          />
          <button
            type="button"
            onClick={() => avatarInputRef.current?.click()}
            disabled={uploadingAvatar}
            aria-label="Change profile photo"
            className="w-16 h-16 rounded-full bg-brand-accent flex items-center justify-center text-white text-2xl font-bold overflow-hidden cursor-pointer"
          >
            {avatarUrl ? (
              <img src={avatarUrl} alt="" className="w-full h-full object-cover" />
            ) : (
              initial
            )}
          </button>
          {uploadingAvatar && (
            <span className="absolute inset-0 flex items-center justify-center bg-black/40 rounded-full pointer-events-none">
              <Loader2 className="w-5 h-5 text-white animate-spin" />
            </span>
          )}
          <span
            className="absolute -bottom-1 -right-1 w-6 h-6 rounded-full flex items-center justify-center border-2 shadow-sm"
            style={{ background: 'var(--color-brand-accent)', borderColor: 'var(--color-brand-card)' }}
            aria-hidden="true"
          >
            <Camera className="w-3 h-3 text-white" />
          </span>
        </div>
        <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-lg font-bold truncate" style={{ color: 'var(--color-brand-text)' }}>
              {displayName}
            </h1>
            <svg width="16" height="16" viewBox="0 0 24 24" aria-label="Verified" className="flex-shrink-0">
              <path fill="#E23A82" d="M12 1l2.4 2.1 3.1-.6 1.1 3 3 1.1-.6 3.1L23 12l-2.1 2.4.6 3.1-3 1.1-1.1 3-3.1-.6L12 23l-2.4-2.1-3.1.6-1.1-3-3-1.1.6-3.1L1 12l2.1-2.4-.6-3.1 3-1.1 1.1-3 3.1.6z" />
              <path fill="#fff" d="M9.8 12.6l-1.9-1.9-1.2 1.2 3.1 3.1 6-6-1.2-1.2z" />
            </svg>
            {isPremium && (
              <span className="flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold tracking-widest uppercase bg-amber-400/10 text-amber-400 border border-amber-400/20">
                <Crown className="w-3 h-3" /> Premium
              </span>
            )}
          </div>
          <p className="text-sm text-zinc-500 truncate">{email}</p>
          <p className="text-xs text-zinc-600 mt-0.5">Member since {joinDate}</p>
          {avatarError && <p className="text-xs text-red-500 mt-1">{avatarError}</p>}
        </div>
      </div>

      {/* Navigation */}
      <Section label="Account">
        <NavItem
          icon={<LayoutDashboard className="w-4 h-4" />}
          label="My Sessions"
          sub="View all your active sessions"
          to="/dashboard"
        />
        <NavItem
          icon={<Mail className="w-4 h-4" />}
          label="Contact Support"
          sub="new@yhuu.life"
          href="mailto:new@yhuu.life"
        />
      </Section>

      {/* Yhuu Test — compatibility quiz (separate app/database, launched with this account) */}
      <Section label="Yhuu Test">
        <a
          href={testLink}
          className="flex items-center gap-3 px-4 py-3.5 hover:bg-brand-accent/5 transition-colors group"
        >
          <span
            className="w-8 h-8 rounded-lg flex items-center justify-center border border-brand-border text-zinc-400 group-hover:text-brand-accent transition-colors flex-shrink-0"
            style={{ background: 'var(--color-brand-bg)' }}
          >
            <Sparkles className="w-4 h-4" />
          </span>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-medium" style={{ color: 'var(--color-brand-text)' }}>Take the Yhuu Test</p>
            <p className="text-xs text-zinc-500">See how compatible you are with someone</p>
          </div>
          <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-brand-accent transition-colors" />
        </a>
      </Section>

      {quizHistoryLoading ? (
        <p className="text-xs text-zinc-600 px-1">Loading your Yhuu Test history…</p>
      ) : quizHistory.length > 0 ? (
        <div className="space-y-2">
          <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500 px-1">Your Yhuu Test History</p>
          <div className="rounded-2xl border border-brand-border overflow-hidden divide-y divide-brand-border" style={{ background: 'var(--color-brand-card)' }}>
            {quizHistory.map((session) => {
              const response = session.public_responses?.[0];
              return (
                <div key={session.id} className="flex items-center justify-between px-4 py-3.5">
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate" style={{ color: 'var(--color-brand-text)' }}>
                      {session.category} {session.partner_name ? `· ${session.partner_name}` : ''}
                    </p>
                    <p className="text-xs text-zinc-500">
                      {new Date(session.created_at).toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })}
                      {response ? ` · answered by ${response.responder_name}` : ' · awaiting response'}
                    </p>
                  </div>
                  <div className="flex items-center flex-shrink-0 ml-3 gap-1">
                    {response && (
                      <span className="text-xs font-bold text-brand-accent flex-shrink-0">
                        {response.score}/{response.total}
                      </span>
                    )}
                    <button
                      onClick={() => handleDeleteQuiz(session.id)}
                      disabled={deletingQuizId === session.id}
                      className="flex-shrink-0 ml-2 w-7 h-7 rounded-lg flex items-center justify-center text-zinc-500 hover:text-red-500 hover:bg-red-500/10 transition-colors disabled:opacity-40"
                      aria-label="Delete this Yhuu Test"
                    >
                      {deletingQuizId === session.id ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Trash2 className="w-3.5 h-3.5" />
                      )}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      ) : null}

      {/* Theme */}
      <Section label="Appearance">
        <div
          className="flex items-center justify-between px-4 py-3.5 rounded-xl border border-brand-border"
          style={{ background: 'var(--color-brand-bg)' }}
        >
          <div className="flex items-center gap-3">
            <span className="w-8 h-8 rounded-lg flex items-center justify-center border border-brand-border" style={{ background: 'var(--color-brand-card)' }}>
              {theme === 'dark' ? <Moon className="w-4 h-4 text-zinc-400" /> : <Sun className="w-4 h-4 text-amber-400" />}
            </span>
            <div>
              <p className="text-sm font-medium" style={{ color: 'var(--color-brand-text)' }}>
                {theme === 'dark' ? 'Dark Mode' : 'Light Mode'}
              </p>
              <p className="text-xs text-zinc-500">Switch app appearance</p>
            </div>
          </div>
          <button
            onClick={toggleTheme}
            className={`relative w-11 h-6 rounded-full transition-colors duration-300 cursor-pointer ${theme === 'dark' ? 'bg-brand-accent' : 'bg-zinc-300'}`}
          >
            <span
              className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-white shadow transition-transform duration-300 ${theme === 'dark' ? 'translate-x-5' : 'translate-x-0'}`}
            />
          </button>
        </div>
      </Section>

      {/* Legal */}
      <Section label="Legal">
        <NavItem icon={<Info className="w-4 h-4" />} label="About Yhuu" to="/about" />
        <NavItem icon={<ShieldCheck className="w-4 h-4" />} label="Safety & Security" to="/safety" />
        <NavItem icon={<FileText className="w-4 h-4" />} label="Terms of Service" to="/terms" />
        <NavItem icon={<FileText className="w-4 h-4" />} label="Privacy Policy" to="/privacy" />
      </Section>

      {/* Logout */}
      <button
        onClick={handleLogout}
        className="w-full flex items-center justify-center gap-2 py-3.5 rounded-xl border border-brand-accent/30 text-brand-accent text-sm font-semibold hover:bg-brand-accent/5 transition-colors cursor-pointer"
      >
        <LogOut className="w-4 h-4" />
        Sign Out
      </button>

      <p className="text-center text-xs text-zinc-600 pb-4">Yhuu · Established for Relationship Integrity</p>
    </div>
  );
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="space-y-2">
      <p className="text-[10px] font-bold tracking-[0.15em] uppercase text-zinc-500 px-1">{label}</p>
      <div className="rounded-2xl border border-brand-border overflow-hidden divide-y divide-brand-border" style={{ background: 'var(--color-brand-card)' }}>
        {children}
      </div>
    </div>
  );
}

function NavItem({
  icon, label, sub, to, href,
}: {
  icon: React.ReactNode;
  label: string;
  sub?: string;
  to?: string;
  href?: string;
}) {
  const inner = (
    <div className="flex items-center gap-3 px-4 py-3.5 hover:bg-brand-accent/5 transition-colors group">
      <span className="w-8 h-8 rounded-lg flex items-center justify-center border border-brand-border text-zinc-400 group-hover:text-brand-accent transition-colors flex-shrink-0"
        style={{ background: 'var(--color-brand-bg)' }}>
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ color: 'var(--color-brand-text)' }}>{label}</p>
        {sub && <p className="text-xs text-zinc-500">{sub}</p>}
      </div>
      <ChevronRight className="w-4 h-4 text-zinc-600 group-hover:text-brand-accent transition-colors" />
    </div>
  );

  if (href) return <a href={href}>{inner}</a>;
  if (to) return <Link to={to}>{inner}</Link>;
  return <div>{inner}</div>;
}
